import React, { useState, useEffect } from 'react';
import axios from 'axios';
import store from '../../store/index';
import SpinnerLoader from '../../components/common/SpinnerLoader';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import DataTable from 'react-data-table-component';
import DataTableExtensions from "react-data-table-component-extensions";
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import Header from '../../components/admin/common/Header';
import Footer from '../../components/common/Footer';
import { Link } from "react-router-dom";
import arrow_back from '../../assets/icons/arrow-back.svg';
import { Modal } from 'react-bootstrap';

const ManageCurrencies = () => {
    const authInfo = store.getState().auth.authInfo;
    const [loading, setLoading] = useState(false);
    const [currencyList, setCurrencyList] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [currentCurrency, setCurrentCurrency] = useState(null);
    const [permissions, setPermissions] = useState({
        add: false,
        edit: false,
        delete: false
    });
    const [modalShow, setModalShow] = useState(false);
    const [editModalShow, setEditModalShow] = useState(false);
    const [stripeCurrencies, setStripeCurrencies] = useState([]);

    // Define columns
    const currency_columns = [
        {
            name: 'Currency Code',
            selector: (row) => row.code?.toUpperCase(),
            sortable: true,
            width: '120px'
        },
        {
            name: 'Currency Name',
            selector: (row) => row.name,
            sortable: true,
            width: '200px'
        },
        {
            name: 'Symbol',
            selector: (row) => row.symbol,
            sortable: true,
            width: '100px',
            center: true
        },
        {
            name: 'Symbol Position',
            selector: (row) => row.symbolPosition === 'before' ? 'Before Amount' : 'After Amount',
            sortable: true,
            width: '150px'
        },
        {
            name: 'Decimal Places',
            selector: (row) => row.decimalPlaces,
            sortable: true,
            width: '130px',
            center: true
        },
        {
            name: 'Default Currency',
            selector: (row) => (
                <span className={`badge ${row.isDefault ? 'bg-primary' : 'bg-secondary'}`}>
                    {row.isDefault ? 'Default' : 'Not Default'}
                </span>
            ),
            sortable: true,
            width: '150px'
        },
        {
            name: 'Status',
            selector: (row) => (
                <span className={`badge ${row.isActive ? 'bg-success' : 'bg-danger'}`}>
                    {row.isActive ? 'Active' : 'Inactive'}
                </span>
            ),
            sortable: true,
            width: '100px'
        },
        {
            name: 'Created Date',
            selector: (row) => new Date(row.createdAt).toLocaleDateString(),
            sortable: true,
            width: '120px'
        },
        {
            name: "Actions",
            cell: (row) => {
                return (
                    <div className="d-flex gap-2">
                        <button
                            className="custom_btn btn_yellow_bordered w-auto btn btn-width action_btn_new"
                            onClick={() => handleEdit(row)}
                            disabled={!permissions.edit}
                        >
                            Edit
                        </button>
                        {!row.isDefault && (
                            <button
                                className="custom_btn btn_success_bordered w-auto btn btn-width action_btn_new"
                                onClick={() => handleSetDefault(row)}
                                disabled={!permissions.edit}
                            >
                                Set Default
                            </button>
                        )}
                        <button
                            className={`custom_btn w-auto btn btn-width action_btn_new ${row.isActive ? 'btn_danger_bordered' : 'btn_success_bordered'
                                }`}
                            onClick={() => handleChangeStatus(row, !row.isActive)}
                            disabled={row.isDefault && row.isActive} // Prevent disabling default currency
                        >
                            {row.isActive ? 'Disable' : 'Enable'}
                        </button>
                    </div>
                );
            },
            width: '300px'
        }
    ];

    useEffect(() => {
        getCurrencyPermissions();
        fetchCurrencyList();
        fetchStripeCurrencies();
    }, []);

    const getCurrencyPermissions = async () => {
        const admin_Id = authInfo.id;
        try {
            const res = await axios.get(`admin/getCurrencyPermission/${admin_Id}`, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`
                }
            });

            if (res.data.status === true && res.data.data) {
                setPermissions(res.data.data);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching permissions');
            console.error("Error fetching permissions: ", error);
        }
    };

    const fetchCurrencyList = async () => {
        setLoading(true);
        try {
            const response = await axios.get("/admin/currencies", {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`
                }
            });
            setCurrencyList(response.data.data || []);
        } catch (error) {
            console.error("Error fetching currency data", error);
            toast.error("Error fetching currency data");
        } finally {
            setLoading(false);
        }
    };

    const fetchStripeCurrencies = async () => {
        try {
            const response = await axios.get("/admin/stripe-supported-currencies", {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`
                }
            });
            setStripeCurrencies(response.data.data || []);
        } catch (error) {
            console.error("Error fetching Stripe currencies", error);
        }
    };

    const saveCurrency = async (values, { setSubmitting, resetForm }) => {
        try {
            const currencyData = {
                ...values,
                admin_id: authInfo.id
            };

            const response = await axios.post("/admin/currencies", currencyData, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`
                }
            });

            if (response.data.status === false) {
                toast.error(response.data.message);
            } else {
                toast.success(response.data.message);
                await fetchCurrencyList();
                resetForm();
                setModalShow(false);
            }
        } catch (error) {
            console.error("Error saving currency", error);
            toast.error(error.response?.data?.message || "Error saving currency");
        } finally {
            setSubmitting(false);
        }
    };

    const updateCurrency = async (values, { setSubmitting }) => {
        try {
            const response = await axios.put(`/admin/currencies/${currentCurrency._id}`, values, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`
                }
            });

            if (response.data.status === true) {
                toast.success(response.data.message);
                await fetchCurrencyList();
                setCurrentCurrency(null);
                setEditModalShow(false);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Error updating currency', error);
            toast.error(error.response?.data?.message || 'Error updating currency');
        } finally {
            setSubmitting(false);
        }
    };

    const handleChangeStatus = async (row, isActive) => {
        try {
            const response = await axios.put(`/admin/currencies/status/${row._id}`, { isActive }, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`
                }
            });

            if (response.data.status === true) {
                toast.success(response.data.message);
                await fetchCurrencyList();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error("Error changing status", error);
            toast.error(error.response?.data?.message || "Error changing status");
        }
    };

    const handleSetDefault = async (row) => {
        try {
            const response = await axios.put(`/admin/currencies/set-default/${row._id}`, {}, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`
                }
            });

            if (response.data.status === true) {
                toast.success(response.data.message);
                await fetchCurrencyList();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error("Error setting default currency", error);
            toast.error(error.response?.data?.message || "Error setting default currency");
        }
    };

    const handleRowSelected = (state) => {
        setSelectedRows(state.selectedRows);
    };

    const handleEdit = (row) => {
        setCurrentCurrency(row);
        setEditModalShow(true);
    };

    const handleAddCurrency = () => {
        setModalShow(true);
    };

    const handleCloseModal = () => {
        setModalShow(false);
    };

    const handleCloseEditModal = () => {
        setEditModalShow(false);
        setCurrentCurrency(null);
    };

    // Form validation schemas
    const addValidationSchema = Yup.object({
        code: Yup.string()
            .required('Currency code is required'),
        name: Yup.string()
            .required('Currency name is required')
            .min(2, 'Currency name must be at least 2 characters'),
        symbol: Yup.string()
            .required('Currency symbol is required'),
        symbolPosition: Yup.string()
            .required('Symbol position is required')
            .oneOf(['before', 'after'], 'Symbol position must be before or after'),
        decimalPlaces: Yup.number()
            .required('Decimal places is required')
            .min(0, 'Decimal places cannot be negative')
            .max(4, 'Decimal places cannot exceed 4')
    });

    const editValidationSchema = Yup.object({
        name: Yup.string()
            .required('Currency name is required')
            .min(2, 'Currency name must be at least 2 characters'),
        symbol: Yup.string()
            .required('Currency symbol is required'),
        symbolPosition: Yup.string()
            .required('Symbol position is required')
            .oneOf(['before', 'after'], 'Symbol position must be before or after'),
        decimalPlaces: Yup.number()
            .required('Decimal places is required')
            .min(0, 'Decimal places cannot be negative')
            .max(4, 'Decimal places cannot exceed 4')
    });

    const initialAddValues = {
        code: '',
        name: '',
        symbol: '',
        symbolPosition: 'before',
        decimalPlaces: 2
    };

    return (
        <React.Fragment>
            <div className="seller_dash_wrap pb-5">
                <div className="container">
                    <Header />
                    <Helmet>
                        <title>{"Admin - Manage Currencies - Pay Earth"}</title>
                    </Helmet>
                    <div className="inr_top_page_title">
                        <h2>Manage Currencies</h2>
                    </div>

                    {/* Add Currency Modal */}
                    <Modal show={modalShow} onHide={handleCloseModal} size="lg">
                        <div className="createpost bg-white rounded-3 addPost_left_container">
                            <Modal.Header closeButton>
                                <div className="cp_top">
                                    <Modal.Title>
                                        <div className="cumm_title">
                                            ADD NEW CURRENCY
                                        </div>
                                    </Modal.Title>
                                </div>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="cp_body">
                                    <Formik
                                        initialValues={initialAddValues}
                                        validationSchema={addValidationSchema}
                                        onSubmit={saveCurrency}
                                    >
                                        {({ isSubmitting, values, setFieldValue }) => (
                                            <Form>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="crt_bnr_fieldRow">
                                                            <div className="crt_bnr_field">
                                                                <label htmlFor="code">Currency Code</label>
                                                                <div className="field_item">
                                                                    <Field
                                                                        as="select"
                                                                        className="form-control"
                                                                        name="code"
                                                                        onChange={(e) => {
                                                                            const selectedCurrency = stripeCurrencies.find(
                                                                                curr => curr.code === e.target.value
                                                                            );
                                                                            setFieldValue('code', e.target.value);
                                                                            if (selectedCurrency) {
                                                                                setFieldValue('name', selectedCurrency.name);
                                                                                setFieldValue('symbol', selectedCurrency.symbol);
                                                                                setFieldValue('decimalPlaces', selectedCurrency.decimalPlaces);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <option value="">Select Currency Code</option>
                                                                        {stripeCurrencies.map((currency) => (
                                                                            <option key={currency.code} value={currency.code}>
                                                                                {currency.code.toUpperCase()} - {currency.name}
                                                                            </option>
                                                                        ))}
                                                                    </Field>
                                                                    <ErrorMessage name="code" component="div" className="text-danger" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="crt_bnr_fieldRow">
                                                            <div className="crt_bnr_field">
                                                                <label htmlFor="name">Currency Name</label>
                                                                <div className="field_item">
                                                                    <Field
                                                                        className="form-control"
                                                                        type="text"
                                                                        name="name"
                                                                    />
                                                                    <ErrorMessage name="name" component="div" className="text-danger" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="crt_bnr_fieldRow">
                                                            <div className="crt_bnr_field">
                                                                <label htmlFor="symbol">Currency Symbol</label>
                                                                <div className="field_item">
                                                                    <Field
                                                                        className="form-control"
                                                                        type="text"
                                                                        name="symbol"
                                                                    />
                                                                    <ErrorMessage name="symbol" component="div" className="text-danger" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="crt_bnr_fieldRow">
                                                            <div className="crt_bnr_field">
                                                                <label htmlFor="symbolPosition">Symbol Position</label>
                                                                <div className="field_item">
                                                                    <Field
                                                                        as="select"
                                                                        className="form-control"
                                                                        name="symbolPosition"
                                                                    >
                                                                        <option value="before">Before Amount</option>
                                                                        <option value="after">After Amount</option>
                                                                    </Field>
                                                                    <ErrorMessage name="symbolPosition" component="div" className="text-danger" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="crt_bnr_fieldRow">
                                                            <div className="crt_bnr_field">
                                                                <label htmlFor="decimalPlaces">Decimal Places</label>
                                                                <div className="field_item">
                                                                    <Field
                                                                        className="form-control"
                                                                        type="number"
                                                                        name="decimalPlaces"
                                                                        min="0"
                                                                        max="4"
                                                                    />
                                                                    <ErrorMessage name="decimalPlaces" component="div" className="text-danger" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="filter_btn_box text-center">
                                                    <button
                                                        className='btn custom_btn btn_yellow_bordered'
                                                        type="submit"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? "Adding..." : "Add Currency"}
                                                    </button>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </Modal.Body>
                        </div>
                    </Modal>

                    {/* Edit Currency Modal */}
                    <Modal show={editModalShow} onHide={handleCloseEditModal} size="lg">
                        <div className="createpost bg-white rounded-3 addPost_left_container">
                            <Modal.Header closeButton>
                                <div className="cp_top">
                                    <Modal.Title>
                                        <div className="cumm_title">
                                            EDIT CURRENCY
                                        </div>
                                    </Modal.Title>
                                </div>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="cp_body">
                                    {currentCurrency && (
                                        <Formik
                                            enableReinitialize={true}
                                            initialValues={{
                                                name: currentCurrency.name || '',
                                                symbol: currentCurrency.symbol || '',
                                                symbolPosition: currentCurrency.symbolPosition || 'before',
                                                decimalPlaces: currentCurrency.decimalPlaces || 2
                                            }}
                                            validationSchema={editValidationSchema}
                                            onSubmit={updateCurrency}
                                        >
                                            {({ isSubmitting }) => (
                                                <Form>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="crt_bnr_fieldRow">
                                                                <div className="crt_bnr_field">
                                                                    <label>Currency Code</label>
                                                                    <div className="field_item">
                                                                        <input
                                                                            className="form-control"
                                                                            type="text"
                                                                            value={currentCurrency.code?.toUpperCase()}
                                                                            disabled
                                                                        />
                                                                        <small className="text-muted">Currency code cannot be changed</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="crt_bnr_fieldRow">
                                                                <div className="crt_bnr_field">
                                                                    <label htmlFor="name">Currency Name</label>
                                                                    <div className="field_item">
                                                                        <Field
                                                                            className="form-control"
                                                                            type="text"
                                                                            name="name"
                                                                        />
                                                                        <ErrorMessage name="name" component="div" className="text-danger" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="crt_bnr_fieldRow">
                                                                <div className="crt_bnr_field">
                                                                    <label htmlFor="symbol">Currency Symbol</label>
                                                                    <div className="field_item">
                                                                        <Field
                                                                            className="form-control"
                                                                            type="text"
                                                                            name="symbol"
                                                                        />
                                                                        <ErrorMessage name="symbol" component="div" className="text-danger" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="crt_bnr_fieldRow">
                                                                <div className="crt_bnr_field">
                                                                    <label htmlFor="symbolPosition">Symbol Position</label>
                                                                    <div className="field_item">
                                                                        <Field
                                                                            as="select"
                                                                            className="form-control"
                                                                            name="symbolPosition"
                                                                        >
                                                                            <option value="before">Before Amount</option>
                                                                            <option value="after">After Amount</option>
                                                                        </Field>
                                                                        <ErrorMessage name="symbolPosition" component="div" className="text-danger" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="crt_bnr_fieldRow">
                                                                <div className="crt_bnr_field">
                                                                    <label htmlFor="decimalPlaces">Decimal Places</label>
                                                                    <div className="field_item">
                                                                        <Field
                                                                            className="form-control"
                                                                            type="number"
                                                                            name="decimalPlaces"
                                                                            min="0"
                                                                            max="4"
                                                                        />
                                                                        <ErrorMessage name="decimalPlaces" component="div" className="text-danger" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="filter_btn_box text-center">
                                                        <button
                                                            type="button"
                                                            className='btn custom_btn btn_yellow_bordered me-2'
                                                            onClick={handleCloseEditModal}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            className='btn custom_btn btn_yellow_bordered'
                                                            type="submit"
                                                            disabled={isSubmitting}
                                                        >
                                                            {isSubmitting ? "Updating..." : "Update Currency"}
                                                        </button>
                                                    </div>
                                                </Form>
                                            )}
                                        </Formik>
                                    )}
                                </div>
                            </Modal.Body>
                        </div>
                    </Modal>

                    {/* Currency List */}
                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="createpost bg-white rounded-3 addPost_left_container">
                                <div className="cp_top">
                                    <div className="d-flex ml-auto justify-content-between gap-2">
                                        <div className="cumm_title">Currency List</div>
                                        <div className="d-flex justify-content-end ml-auto gap-2">
                                            <Link
                                                className={`btn custom_btn mx-auto ${permissions.add ? "btn_yellow" : "btn_disabled"
                                                    }`}
                                                to="#"
                                                onClick={permissions.add ? handleAddCurrency : null}
                                            >
                                                Add Currency
                                            </Link>
                                            <div className=''>
                                                <button
                                                    type="button"
                                                    className="btn custum_back_btn btn_yellow mx-auto"
                                                    onClick={() => window.history.back()}
                                                >
                                                    <img src={arrow_back} alt="back" />&nbsp;
                                                    Back
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="cp_body">
                                    {loading && <SpinnerLoader />}
                                    <DataTableExtensions
                                        columns={currency_columns}
                                        data={currencyList}
                                    >
                                        <DataTable
                                            pagination
                                            noHeader
                                            highlightOnHover
                                            defaultSortField="createdAt"
                                            defaultSortAsc={false}
                                            selectableRows
                                            selectedRows={selectedRows}
                                            onSelectedRowsChange={handleRowSelected}
                                        />
                                    </DataTableExtensions>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </React.Fragment>
    );
};

export default ManageCurrencies;